/**
 * Insight engine — pure. `evaluate` recomputes candidate insights from context;
 * `reconcile` diffs against the previous set to preserve user lifecycle (ack /
 * dismiss) by stable id and auto-resolve cards whose trigger has cleared, while
 * emitting audit events. `rank` + `visible` keep the stream glanceable.
 *
 * Priority ordering (P1 FF safety → P2 others → P3 property → P4 normality)
 * is the primary sort key. P1/P2 open items suppress P3/P4 below a visual fold.
 */

import type { NewAuditEvent } from '../audit.js';
import { stageIndex } from '../stages.js';
import { activeIncidentTypes } from '../incident.js';
import { RULES } from './rules.js';
import type {
  Insight,
  InsightContext,
  InsightSeverity,
  InsightStatus,
  OperationalPriority,
} from './types.js';
import type { Confidence } from '../fact.js';

const PRIORITY_WEIGHT: Record<OperationalPriority, number> = {
  1: 10000,
  2: 1000,
  3: 100,
  4: 10,
};
const SEVERITY_WEIGHT: Record<InsightSeverity, number> = {
  critical: 8,
  high: 4,
  medium: 2,
  info: 1,
};
const CONFIDENCE_WEIGHT: Record<Confidence, number> = { high: 3, medium: 2, low: 1 };

/**
 * Run every rule; skip rules whose minStage hasn't been reached yet or whose
 * incidentTypes list shares nothing with the incident's currently-active
 * types (primary + any simultaneously co-active additional types — see
 * `activeIncidentTypes`), so a rule scoped to a type that only became
 * relevant after creation (e.g. GrassFire added once a vehicle fire spreads)
 * still fires without needing the incident's primary type changed.
 * Returns triggered insights (status `open`).
 */
export function evaluate(ctx: InsightContext, rules = RULES): Insight[] {
  const out: Insight[] = [];
  const currentStageIdx = stageIndex(ctx.incident.stage);
  const incidentTypes = activeIncidentTypes(ctx.incident);
  for (const rule of rules) {
    if (rule.minStage !== undefined && currentStageIdx < stageIndex(rule.minStage)) continue;
    if (rule.incidentTypes !== undefined && !rule.incidentTypes.some((t) => incidentTypes.includes(t))) continue;
    const result = rule.evaluate(ctx);
    if (!result) continue;
    out.push({
      ...result,
      id: rule.id,
      ruleId: rule.id,
      status: 'open',
      firstSeenAtMs: ctx.nowMs,
      lastEvaluatedAtMs: ctx.nowMs,
    });
  }
  return out;
}

/**
 * Higher score = more important.
 * Operational priority dominates; severity × confidence breaks ties within a tier.
 */
export function score(insight: Insight): number {
  return (
    PRIORITY_WEIGHT[insight.operationalPriority] +
    SEVERITY_WEIGHT[insight.severity] * CONFIDENCE_WEIGHT[insight.confidence]
  );
}

export function rank(insights: Insight[]): Insight[] {
  return [...insights].sort((a, b) => score(b) - score(a) || a.id.localeCompare(b.id));
}

/**
 * The cards to show now — ranked, dismissed/resolved hidden, capped.
 *
 * Suppression: if any P1 or P2 insight is open+unacknowledged, P3/P4 insights
 * are marked `suppressed: true` and returned separately. The web layer decides
 * whether to show the fold. Acked/actioned cards are never suppressed (the
 * officer already dealt with them).
 */
export interface VisibleResult {
  /** Insights to pin at the top (up to max). */
  shown: Insight[];
  /** P3/P4 insights held below the fold because a P1/P2 item is open. */
  suppressed: Insight[];
  /** True when suppression is active — lets the UI render the fold message. */
  suppressedByPriority: boolean;
}

export function visible(insights: Insight[], max: number): VisibleResult {
  const eligible = insights.filter(
    (i) => i.status === 'open' || i.status === 'acknowledged' || i.status === 'actioned',
  );
  const ranked = rank(eligible);

  const hasOpenHighPriority = ranked.some(
    (i) => (i.operationalPriority === 1 || i.operationalPriority === 2) && i.status === 'open',
  );

  const suppressed: Insight[] = [];
  const candidates: Insight[] = [];

  for (const i of ranked) {
    const isSuppressible =
      hasOpenHighPriority &&
      (i.operationalPriority === 3 || i.operationalPriority === 4) &&
      i.status === 'open';

    if (isSuppressible) {
      suppressed.push({ ...i, suppressed: true });
    } else {
      candidates.push(i);
    }
  }

  return {
    shown: candidates.slice(0, Math.max(0, max)),
    suppressed,
    suppressedByPriority: hasOpenHighPriority && suppressed.length > 0,
  };
}

const USER_STATUSES: InsightStatus[] = ['acknowledged', 'actioned', 'dismissed'];

export interface ReconcileResult {
  /** Currently-triggered insights, carrying preserved user lifecycle. */
  active: Insight[];
  events: NewAuditEvent[];
}

/**
 * Merge freshly-evaluated candidates with the previous set:
 *  - a candidate seen before keeps its user status + firstSeen (content refreshed);
 *  - a brand-new candidate is `open` and emits `insight.shown`;
 *  - a previous insight no longer triggered emits `insight.auto-resolved`.
 */
export function reconcile(
  previous: Insight[],
  candidates: Insight[],
  incidentId: string,
  nowMs: number,
): ReconcileResult {
  const prevById = new Map(previous.map((i) => [i.id, i]));
  const candidateIds = new Set(candidates.map((c) => c.id));
  const active: Insight[] = [];
  const events: NewAuditEvent[] = [];

  for (const cand of candidates) {
    const prev = prevById.get(cand.id);
    if (prev) {
      const status: InsightStatus = USER_STATUSES.includes(prev.status) ? prev.status : 'open';
      active.push({
        ...cand,
        status,
        dismissReason: prev.dismissReason,
        firstSeenAtMs: prev.firstSeenAtMs,
        lastEvaluatedAtMs: nowMs,
      });
    } else {
      active.push({ ...cand, firstSeenAtMs: nowMs, lastEvaluatedAtMs: nowMs });
      events.push({
        incidentId,
        type: 'insight.shown',
        actor: 'system',
        source: 'derived',
        payload: { insightId: cand.id, severity: cand.severity, title: cand.title },
      });
    }
  }

  for (const prev of previous) {
    if (!candidateIds.has(prev.id) && prev.status !== 'auto-resolved') {
      events.push({
        incidentId,
        type: 'insight.auto-resolved',
        actor: 'system',
        source: 'derived',
        payload: { insightId: prev.id, title: prev.title },
      });
    }
  }

  return { active, events };
}

/** Apply a user lifecycle transition to one insight (immutably). */
export function applyUserAction(
  insights: Insight[],
  insightId: string,
  status: Extract<InsightStatus, 'acknowledged' | 'actioned' | 'dismissed'>,
  dismissReason?: string,
): Insight[] {
  return insights.map((i) =>
    i.id === insightId ? { ...i, status, dismissReason: status === 'dismissed' ? dismissReason : undefined } : i,
  );
}
